
import { BankType } from "@/models/base";
import { BankService } from "@/services/bank-service";
import Vue from "vue";
import { IBreadCrumb, ISelectItem } from "winfakt-vue-components";
import { DossierConfigService } from "../../../services/dossier-config-service";
import { UserConfigService } from "../../../services/user-config-service";
import AppMollieSettings from "@/components/settings-default/MollieSettings.vue";
import AppSelectBankType from "@/components/bank/SelectBankType.vue";
import { AuthService } from "@/services/auth-service";
import { MODULE } from "@/config/modules";
import AppBankConfigureCtepTerminal from "@/components/settings-default/BankConfigureCtepTerminal.vue";
import { BrowserInfo } from "@/utils/browser";
import AppBankConfigureCtepBankTypePerBrandSettings from "@/components/settings-default/BankConfigureCtepBankTypePerBrandSettings.vue";
import AppBankConfigureTerminalBookings from "@/components/settings-default/BankConfigureTerminalBookings.vue";
import AppBankConfigureMollieTerminal from "@/components/settings-default/BankConfigureMollieTerminal.vue";
import AppSaleTypeMultiSelect from "@/components/sales/SaleTypeMultiSelect.vue";
import AppPaymentMeansCodeSelect from "@/components/settings-default/PaymentMeansCodeSelect.vue";

export default Vue.extend({
	data() {
		return {
			types: [] as BankType[],
			toEditType: null as null | BankType,
			generalSettings: UserConfigService.getGeneralSettings(),
			generalDossierSettings: DossierConfigService.getGeneralConfig(),
			bankConfig: DossierConfigService.getBankConfig(),
			isSaving: false,
			modalTab: 0
		};
	},
	computed: {
		path():IBreadCrumb[] {
			return [{
				to: "/settings", value: this.$ct("general")
			},      {value: this.$ct("bank")}] as IBreadCrumb[];
		},
		modalTabs():any {
			return this.$ct("bank-type-modal-tabs");
		},
		modalTitle():string {
			if (!this.toEditType) {
				return "";
			}
			if (this.toEditType.ID == 0) {
				return this.$ct("modal-title-new").toString();
			}
			return this.$ct("modal-title-edit").toString();
		},
		sepaPainVersionItems():ISelectItem<string>[] {
			return [
				{
					text: this.$ct("sepa-pain-version-03").toString(),
					value: "03"
				},
				{
					text: this.$ct("sepa-pain-version-09").toString(),
					value: "09"
				}
			];
		},
		bankTypeSelectItems():ISelectItem<number>[] {
			return this.types.map(t=>{
				return {
					text: t.Name,
					value: t.ID,
				};
			});
		},
		maxBankTypesReached():boolean {
			if (AuthService.hasModule(MODULE.TOT_99_BANKEN)){
				return this.types.length >= 99;
			}
			return this.types.length >= 10;
		},
		isElectron():boolean {
			return BrowserInfo.hasElectronConfigured();
		},
		canEditSaleTypesForBookings():boolean {
			return AuthService.wfCompany.OwnerID == AuthService.wfUser.ID || UserConfigService.getPermissionSettings().IsAdmin;
		}
	},
	methods: {
		// eslint-disable-next-line
		autoFillBic(){

			if(!this.toEditType?.Iban){
				return;
			}
			const countryCode = this.toEditType?.Iban?.replace(/\s+/g, "").substring(0, 2);

			let bankCode = "";
			if (countryCode === "BE") {
				bankCode = this.toEditType?.Iban?.substring(4, 7);
			} else if (countryCode === "NL") {
				bankCode = this.toEditType?.Iban?.substring(4, 8);
			}

			const bicMapping = require(`@/assets/json/bic-mappings.json`) as any;

			const bicCode = bicMapping[bankCode];

			if (!bicCode) {
				return false;
			}

			this.toEditType.Bic = bicCode;

		},
		async fetch() {
			await BankService.fetchTypes();
			this.types = BankService.getTypes();
		},
		openNewModal() {
			this.modalTab = 0;
			this.toEditType = new BankType();
		},
		cancelModal() {
			this.toEditType = null;
		},
		async save() {
			if (this.isSaving) return;
			this.isSaving = true;
			try{
				if (!this.toEditType) {
					return;
				}
				if (this.toEditType.ID == 0) {
					await BankService.postType(this.toEditType);
				} else {
					await BankService.putType(this.toEditType);
				}
				await this.fetch();
				this.cancelModal();
			}catch(err){
			}finally{
				this.isSaving = false;
			}
		},
		editType(type:BankType) {
			this.modalTab = 0;
			this.toEditType = new BankType(type.getJSON());
		},
		async deleteType(type:BankType) {
			let confirm = await this.$confirmBuilder(this.$ct("confirm-delete", {name: type.Name})).setWarningTitle().show();
			if (confirm) {
				await BankService.deleteType(type);
				await this.fetch();
			}
		},
		async setAsDefault(item:BankType){
			let settings = DossierConfigService.getGeneralConfig();
			settings.DefaultBankTypeID = item.ID;
			await DossierConfigService.saveGeneralConfig(settings);
		},
		isBankTypeDefault(item:BankType):boolean{
			return item.ID == DossierConfigService.getGeneralConfig().DefaultBankTypeID;
		},
		async setRepeatBankSave(val:boolean){
			let settings = UserConfigService.getGeneralSettings();
			settings.RepeatBankSave = val;
			this.generalSettings = await UserConfigService.setGeneralSettings(settings);
		},
		async saveGeneral(){
			if (this.bankConfig.SaleTypeIDsForBookings.length == 0) {
				this.$wf.notify(this.$ct("sale-types-for-bookings-empty").toString(), "danger", 3000, "exclamation-triangle");
				return;
			}
			await UserConfigService.setGeneralSettings(this.generalSettings);
			await DossierConfigService.saveGeneralConfig(this.generalDossierSettings);
			let bankConfig = DossierConfigService.getBankConfig();
			bankConfig.SaleTypeIDsForBookings = this.bankConfig.SaleTypeIDsForBookings;
			await DossierConfigService.setBankConfig(bankConfig);
			this.$wf.notify(this.$ct("common.success").toString(), "success", 3000, "check");
		},
		async updateOrder(){
			let order = this.types.map(t=>t.ID);
			let settings = UserConfigService.getGeneralSettings();
			settings.BanksOrder = order;
			await UserConfigService.setGeneralSettings(settings);
			this.generalSettings.BanksOrder = order;
		}
	},
	async mounted() {
		await this.fetch();
	},
	components: {
		AppPaymentMeansCodeSelect,
		AppMollieSettings,
		AppSelectBankType,
		AppBankConfigureCtepTerminal,
		AppBankConfigureCtepBankTypePerBrandSettings,
		AppBankConfigureTerminalBookings,
		AppBankConfigureMollieTerminal,
		AppSaleTypeMultiSelect
	},
	i18nEx: {
		componentPrefix: "components.settings-default"
	}
});
